import type { MapDef } from './MapDef.js';

// Re-exports kept for backwards compatibility with the previous file layout.
// New code should import these types from './MapDef.js' directly.
export type {
  ObstacleRect,
  CorePlacement,
  ControlNodeName,
  ControlNodePlacement,
  SquadSpawn,
  MapDef,
} from './MapDef.js';

// V1 polished Foundry map. The canonical V0.2 layout (mirrored north/south,
// three relays, four "core obstacles" between Cores and adjacent relays / the
// mid relay) is preserved exactly so V0.2/V0.3/V0.4 pathfinding and bot tests
// keep their fixtures. V1 adds "cover obstacles" — extra walls that create
// flanking lanes around the mid relay, give Engineer mini-turrets places to
// nest, and break the previous "shoot down a single corridor" sightlines.
//
// Authoring rules for any new V1 obstacle:
//   - Stay clear of the relay capture radius (120 px) so capture geometry is
//     untouched.
//   - Stay clear of the build-slot ring around each Core (200 px) so build
//     mode placement isn't blocked by pathing rocks.
//   - Stay clear of squad spawn points so production doesn't pin a unit on a
//     wall.
//   - Add only positive-area rects; obstacles are baked into the pathfinding
//     grid via Math.floor / Math.ceil in pathfinding/Grid.ts.
export const FOUNDRY: MapDef = {
  id: 'foundry',
  name: 'Foundry',
  width: 2000,
  height: 2000,
  tilePx: 32,
  cores: [
    { ownerId: 0, pos: { x: 1000, y: 220 } },
    { ownerId: 1, pos: { x: 1000, y: 1780 } },
  ],
  controlNodes: [
    { name: 'north', pos: { x: 1000, y: 600 } },
    { name: 'mid', pos: { x: 1000, y: 1000 } },
    { name: 'south', pos: { x: 1000, y: 1400 } },
  ],
  obstacles: [
    // V0.2 core obstacles — preserved verbatim. Tests in pathfinding.test.ts
    // and botPlayer.test.ts pin against these positions. Do not move.
    { x: 920, y: 380, w: 160, h: 80 },
    { x: 920, y: 1540, w: 160, h: 80 },
    { x: 720, y: 960, w: 120, h: 80 },
    { x: 1160, y: 960, w: 120, h: 80 },
    // V1 cover walls — rotational symmetry around the map center (1000, 1000).
    // Each pair adds a flanking lane around the mid lane and a rock to hide
    // behind near the north / south relays. Diagonal pairs invite Recon side
    // routes; the cluster near each Core's flank gives Engineer turret nests.
    // North-relay flanking covers (player 0's half).
    { x: 700, y: 540, w: 100, h: 60 },
    { x: 1200, y: 540, w: 100, h: 60 },
    { x: 540, y: 760, w: 80, h: 60 },
    { x: 1380, y: 760, w: 80, h: 60 },
    // South-relay flanking covers (player 1's half — mirror of the above).
    { x: 700, y: 1400, w: 100, h: 60 },
    { x: 1200, y: 1400, w: 100, h: 60 },
    { x: 540, y: 1180, w: 80, h: 60 },
    { x: 1380, y: 1180, w: 80, h: 60 },
    // Mid-lane diagonal walls. Two short diagonals on either side of the mid
    // relay force squads to commit to a flank rather than charge straight
    // through the center; small enough that A* still finds a route.
    { x: 820, y: 880, w: 40, h: 60 },
    { x: 1140, y: 1060, w: 40, h: 60 },
  ],
  spawns: [
    // V1.6 readability — moved Recon spawns ~120 px outward from the Core so
    // they sit clearly outside the Core sprite's halo/bloom. Players were
    // missing the pre-spawned squad because it was visually swallowed by
    // the Core's glow during the tutorial.
    { ownerId: 0, pos: { x: 820, y: 360 } },
    { ownerId: 1, pos: { x: 1180, y: 1640 } },
  ],
  // Mid relay (1000, 1000) — V1.5 neutral camp lives here.
  neutralCampAnchor: { x: 1000, y: 1000 },
};
